import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";
import { useTranslation } from "react-i18next";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { useDebounce } from "../../../../hooks/useDebounce";

import useApi from "../../../../hooks/useApi";
import useCRUDService from "../../../../hooks/useCRUDService";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats, { formatDecimal } from "../../../../utils/helperFormats";

const Applications = () => {
  const APPLICATION_STATE_PENDING = "pending";
  const APPLICATION_STATE_APPROVED = "approved";
  const APPLICATION_STATE_REJECTED = "rejected";

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("applications");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [applicationStateFilter, setApplicationStateFilter] = useState(
    APPLICATION_STATE_PENDING
  );

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "createdDate",
          type: "date",
        },
        {
          name: "amount",
          type: "number",
        },
        {
          name: "paymentsNumber",
          type: "number",
        },
        {
          name: "paymentsAmount",
          type: "number",
        },
      ]);

      const response = await api.get(
        `${env("API_URL")}/applications/${applicationStateFilter}/paged`,
        {
          params: {
            columns,
            page: first,
            pageSize: rows,
            sort: sortField ?? "createdDate",
            order: sortOrder >= 0 ? "ASC" : "DESC",
            globalFilter: debouncedSearchTerm,
          },
        }
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    first,
    rows,
    sortField,
    sortOrder,
    debouncedSearchTerm,
    CRUDService,
    applicationStateFilter,
  ]);

  useEffect(() => {
    fetchData();
  }, [
    first,
    rows,
    sortField,
    sortOrder,
    debouncedSearchTerm,
    applicationStateFilter,
  ]);

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    toast: toast,
    CRUDService: CRUDService,
  });

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary buttonExport"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <div className={s.get("dataTableContainer")}>
      <Toast ref={toast} />
      <DataTable
        onPage={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        ref={dt}
        value={entities}
        lazy
        paginator
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        columnResizeMode="fit"
        showGridlines
        header={header}
        dataKey="id"
        rowsPerPageOptions={[10, 25, 100, 1000]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={`${t(
          "DataTable.currentPageReportTemplate"
        )} ${t("Applications")}`}
        globalFilter={globalFilter}
        className="flex flex-column w-screen ml-2 mr-2"
        emptyMessage={t("DataTable.emptyMessage")}
      >
        <Column
          field="createdDate"
          header={t("entities.applications.createdDate")}
          sortable
          body={(rowData) =>
            helperFormats.formatDate(new Date(rowData.createdDate))
          }
        ></Column>
        <Column
          field="amount"
          header={t("entities.applications.amount")}
          body={(rowData) => helperFormats.formatDecimal(rowData.amount)}
          sortable
        ></Column>
        <Column
          field="paymentsNumber"
          header={t("entities.applications.paymentsNumber")}
          sortable
        ></Column>
        <Column
          field="paymentsAmount"
          header={t("entities.applications.paymentsAmount")}
          body={(rowData) =>
            helperFormats.formatDecimal(rowData.paymentsAmount)
          }
          sortable
        ></Column>
      </DataTable>
    </div>
  );
};

export default Applications;
